import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from '@almothafar/angular-signature-pad';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/core/services/user.service';
import { WithdrawalFlowConfig, WithdrawalQuestion, DynamicAnswers } from 'src/app/core/models/withdrawal-config';

export type WithdrawReason =
  | 'no_longer_interested'
  | 'no_longer_want_representation'
  | 'claimant_passed_away'
  | 'other';

export interface WithdrawFormData {
  contractDate: string;
  clientName: string;
  clientAddress: string;
  clientSignature?: string;
  currentDate: string;
  extraInfo?: DynamicAnswers | {
    cancelAgreement: boolean;
    withdrawFromCase: boolean;
    reason: WithdrawReason;
    otherReasonText?: string;
  };
}

function atLeastOneRequiredValidator(
  control: AbstractControl
): ValidationErrors | null {
  const cancelAgreement = control.get('cancelAgreement')?.value;
  const withdrawFromCase = control.get('withdrawFromCase')?.value;

  if (!cancelAgreement && !withdrawFromCase) {
    return { atLeastOneRequired: true };
  }

  return null;
}

export interface WithdrawFormModalData {
  claimId: string;
  caseName: string;
  isContractDateOver14DaysAgo: boolean;
  contractDate: string | null;
  withdrawalConfig?: WithdrawalFlowConfig | null;
}

interface WithdrawFormControls {
  contractDate: FormControl<string | null>;
  clientName: FormControl<string | null>;
  clientAddress: FormControl<string | null>;
  clientSignature: FormControl<string | null>;
  currentDate: FormControl<string | null>;
  cancelAgreement: FormControl<boolean | null>;
  withdrawFromCase: FormControl<boolean | null>;
  reason: FormControl<WithdrawReason | null>;
  otherReasonText: FormControl<string | null>;
}

const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-withdraw-form-modal',
  templateUrl: './withdraw-form-modal.component.html',
  styleUrls: ['./withdraw-form-modal.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class WithdrawFormModalComponent implements OnInit, AfterViewInit {
  @ViewChild('signature', { static: false })
  public signaturePad?: SignaturePadComponent;

  withdrawForm: FormGroup<WithdrawFormControls>;
  dynamicAnswers: FormGroup = new FormGroup({});
  isSubmitting = false;
  isLoading = true;
  errorMessage: string | null = null;
  maxDate: string;
  recipientAddress: string = '';
  readonly DATE_FORMAT = 'DD/MM/YYYY';

  signaturePadOptions: NgSignaturePadOptions = {
    minWidth: 2,
    canvasWidth: 300,
    canvasHeight: 200,
    backgroundColor: 'white',
    dotSize: 2,
  };

  constructor(
    public dialogRef: MatDialogRef<WithdrawFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WithdrawFormModalData,
    private fb: FormBuilder,
    private userService: UserService,
    private translate: TranslateService
  ) {
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    this.withdrawForm = this.fb.group(
      {
        contractDate: [{ value: '', disabled: true }, [Validators.required]],
        clientName: [{ value: '', disabled: true }, [Validators.required]],
        clientAddress: [{ value: '', disabled: true }, [Validators.required]],
        clientSignature: ['', [Validators.required]],
        currentDate: [
          { value: this.maxDate, disabled: true },
          [Validators.required],
        ],
        cancelAgreement: [false],
        withdrawFromCase: [false],
        reason: [null as WithdrawReason | null, [Validators.required]],
        otherReasonText: [null as string | null],
      },
      { validators: atLeastOneRequiredValidator }
    );
  }

  get config(): WithdrawalFlowConfig | null {
    return this.data.withdrawalConfig ?? null;
  }

  get requireSignature(): boolean {
    if (this.config != null) return this.config.require_signature !== false;
    return true;
  }

  get showCheckboxes(): boolean {
    if (this.config != null) return this.config.require_checkboxes !== false;
    return this.data.isContractDateOver14DaysAgo;
  }

  get showLegacyQuestionnaire(): boolean {
    return this.config == null && this.data.isContractDateOver14DaysAgo;
  }

  get configuredQuestions(): WithdrawalQuestion[] {
    return this.config?.questionnaire ?? [];
  }

  getLocalizedLabel(q: WithdrawalQuestion): string {
    return this.interpolateCaseName(q.label);
  }

  getOptionLabel(option: { value: string; label: string }): string {
    return this.interpolateCaseName(option.label);
  }

  getLocalizedText(text: { en: string; pt: string } | undefined, fallbackKey: string): string {
    const lang = this.translate.currentLang?.startsWith('pt') ? 'pt' : 'en';
    const raw = text ? (text[lang as 'en' | 'pt'] || text.en) : this.translate.instant(fallbackKey);
    return this.interpolateCaseName(raw);
  }

  private interpolateCaseName(text: string): string {
    return text.replace(/\{\{\s*caseName\s*\}\}/g, this.data.caseName ?? '');
  }

  async ngOnInit() {
    try {
      this.loadRecipientInfo();
      await this.loadFormData();
      this.setupReasonChangeHandler();
      this.buildDynamicForm();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load form data';
      this.isLoading = false;
    }
  }

  private buildDynamicForm() {
    if (!this.config?.questionnaire) return;

    for (const question of this.config.questionnaire) {
      const validators = question.required !== false ? [Validators.required] : [];

      if (question.type === 'free_text') {
        const textValidators = [...validators];
        if (question.min_length) textValidators.push(Validators.minLength(question.min_length));
        if (question.max_length) textValidators.push(Validators.maxLength(question.max_length));
        this.dynamicAnswers.addControl(question.id, new FormControl('', textValidators));
      } else {
        this.dynamicAnswers.addControl(question.id, new FormControl(null, validators));
      }
    }
  }

  setupReasonChangeHandler() {
    this.withdrawForm.get('reason')?.valueChanges.subscribe((reason) => {
      const otherReasonControl = this.withdrawForm.get('otherReasonText');
      if (reason === 'other') {
        otherReasonControl?.setValidators([Validators.required, Validators.minLength(15), Validators.maxLength(250)]);
      } else {
        otherReasonControl?.clearValidators();
        otherReasonControl?.setValue(null);
      }
      otherReasonControl?.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    if (this.signaturePad) {
      setTimeout(() => {
        this.signaturePad?.set('minWidth', 2);
        this.signaturePad?.clear();
      }, 100);
    }
  }

  loadRecipientInfo() {
    this.recipientAddress =
      'Mishcon de Reya LLP\nAfrica House\n70 Kingsway\nLondon WC2B 6AH';
  }

  async loadFormData() {
    try {
      this.isLoading = true;

      const currentUser = await this.userService.getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error('User not found');
      }

      const userWithPii = await this.userService.getWithPII(currentUser.id);
      if (!userWithPii || !userWithPii.pii) {
        throw new Error('User PII data not found');
      }

      // For legacy flow: configure form based on contract date
      if (this.config == null) {
        if (!this.data.isContractDateOver14DaysAgo) {
          this.withdrawForm.removeValidators(atLeastOneRequiredValidator);
          this.withdrawForm.get('cancelAgreement')?.disable();
          this.withdrawForm.get('withdrawFromCase')?.disable();
          this.withdrawForm.get('reason')?.disable();
          this.withdrawForm.updateValueAndValidity();
        }
      } else {
        // Config-driven: always disable legacy reason field
        this.withdrawForm.get('reason')?.disable();

        if (this.showCheckboxes) {
          // Keep checkboxes enabled and enforce at-least-one validation
          this.withdrawForm.addValidators(atLeastOneRequiredValidator);
        } else {
          this.withdrawForm.removeValidators(atLeastOneRequiredValidator);
          this.withdrawForm.get('cancelAgreement')?.disable();
          this.withdrawForm.get('withdrawFromCase')?.disable();
        }

        this.withdrawForm.updateValueAndValidity();

        if (!this.requireSignature) {
          this.withdrawForm.get('clientSignature')?.clearValidators();
          this.withdrawForm.get('clientSignature')?.updateValueAndValidity();
        }
      }

      let addressString = '';
      if (userWithPii.pii.primary_address) {
        const addr = userWithPii.pii.primary_address as any;
        const parts = [
          addr.streetName || addr.street,
          addr.streetNumber || addr.number,
          addr.buildingName || addr.complement,
          addr.unitNumber,
          addr.neighborhood,
          addr.city,
          addr.state,
          addr.postalCode || addr.postal_code,
          addr.country,
        ].filter(Boolean);
        addressString = parts.join(', ');
      }

      this.withdrawForm.patchValue({
        contractDate: this.data.contractDate,
        clientName: userWithPii.pii.legal_name || userWithPii.display_name,
        clientAddress: addressString || 'Address not available',
        currentDate: moment(this.maxDate).format(this.DATE_FORMAT),
      });

      this.isLoading = false;
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load form data';
      this.isLoading = false;
      throw error;
    }
  }

  close() {
    if (this.isSubmitting) return;
    this.dialogRef.close();
  }

  clearSignature() {
    if (this.signaturePad && !this.isSubmitting) {
      this.signaturePad.clear();
      this.withdrawForm.get('clientSignature')?.setValue('');
    }
  }

  onSignatureComplete() {
    if (this.signaturePad && !this.signaturePad.isEmpty()) {
      const signatureData = this.signaturePad.toDataURL();
      this.withdrawForm.get('clientSignature')?.setValue(signatureData);
    }
  }

  submit() {
    if (this.requireSignature) {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        this.withdrawForm.get('clientSignature')?.setErrors({ required: true });
        this.withdrawForm.get('clientSignature')?.markAsTouched();
        return;
      }
      const signatureData = this.signaturePad.toDataURL();
      this.withdrawForm.get('clientSignature')?.setValue(signatureData);
    }

    if (this.withdrawForm.invalid || this.dynamicAnswers.invalid) {
      this.withdrawForm.markAllAsTouched();
      this.dynamicAnswers.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const contractDateValue = this.withdrawForm.get('contractDate')?.value;
    let contractDateFormatted = '';
    if (contractDateValue && typeof contractDateValue === 'string') {
      const [day, month, year] = contractDateValue.split('/');
      contractDateFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const formData: WithdrawFormData = {
      contractDate: contractDateFormatted,
      clientName: this.withdrawForm.get('clientName')?.value || '',
      clientAddress: this.withdrawForm.get('clientAddress')?.value || '',
      currentDate: this.maxDate,
    };

    if (this.requireSignature && this.signaturePad && !this.signaturePad.isEmpty()) {
      formData.clientSignature = this.signaturePad.toDataURL();
    }

    if (this.config != null) {
      // Config-driven: collect checkbox values (if shown) and questionnaire answers
      const answers: DynamicAnswers = {};

      if (this.showCheckboxes) {
        answers['cancelAgreement'] = String(this.withdrawForm.get('cancelAgreement')?.value ?? false);
        answers['withdrawFromCase'] = String(this.withdrawForm.get('withdrawFromCase')?.value ?? false);
      }

      for (const question of this.configuredQuestions) {
        answers[question.id] = this.dynamicAnswers.get(question.id)?.value ?? null;
      }

      if (Object.keys(answers).length > 0) {
        formData.extraInfo = answers;
      }
    } else if (this.showLegacyQuestionnaire) {
      // Legacy termination form
      const reasonValue = this.withdrawForm.get('reason')?.value;
      if (!reasonValue) {
        throw new Error('Reason is required');
      }
      formData.extraInfo = {
        cancelAgreement: this.withdrawForm.get('cancelAgreement')?.value || false,
        withdrawFromCase: this.withdrawForm.get('withdrawFromCase')?.value || false,
        reason: reasonValue,
      };
      if (reasonValue === 'other') {
        (formData.extraInfo as any).otherReasonText = this.withdrawForm.get('otherReasonText')?.value || '';
      }
    }

    this.dialogRef.close(formData);
  }

  hasSignature(): boolean {
    if (!this.requireSignature) return true;
    return !!(this.signaturePad && !this.signaturePad.isEmpty());
  }

  toggleMultiChoice(questionId: string, value: string, checked: boolean): void {
    const control = this.dynamicAnswers.get(questionId);
    if (!control) return;
    const current: string[] = control.value || [];
    if (checked) {
      control.setValue([...current, value]);
    } else {
      control.setValue(current.filter((v: string) => v !== value));
    }
  }

  isOtherReasonSelected(): boolean {
    return this.withdrawForm.get('reason')?.value === 'other';
  }

  getModalTitle(): string {
    const config = this.data.withdrawalConfig;
    if (config?.modal_title) {
      const lang = this.translate.currentLang?.startsWith('pt') ? 'pt' : 'en';
      return config.modal_title[lang as 'en' | 'pt'] || config.modal_title.en;
    }
    const key = this.data.isContractDateOver14DaysAgo ? 'TerminationFormTitle' : 'WithdrawFormTitle';
    return this.translate.instant(key);
  }

  getSubmitButtonLabel(): string {
    const config = this.data.withdrawalConfig;
    if (config?.submit_button_label) {
      const lang = this.translate.currentLang?.startsWith('pt') ? 'pt' : 'en';
      return config.submit_button_label[lang as 'en' | 'pt'] || config.submit_button_label.en;
    }
    const key = this.data.isContractDateOver14DaysAgo ? 'RequestToTerminate' : 'SubmitWithdraw';
    return this.translate.instant(key);
  }
}
